import { TbGauge } from 'react-icons/tb'
import { P } from '~/components-v2'
import {
  CompareGrid,
  CompareItem,
  CompareQuote,
  ComparisonCard,
  OmniLogo,
  StrategyLogo,
} from '~/components-v2/compete-comparison-card'

export const CompareCard5 = () => {
  return (
    <ComparisonCard
      className="icon-pink"
      icon={<TbGauge />}
      title="With Omni, you don’t have to compromise on speed or data freshness."
      subhead="Strategy's speed depends on cubes: in-memory copies of your warehouse data that your team must size, schedule, and tune. That’s specialist overhead before a single question gets answered. Omni queries the warehouse directly and lets an intelligent cache handle speed, so there are no cubes to size, schedule, or maintain."
    >
      <CompareGrid
        id="omni-performance"
        compareLogos={[<OmniLogo key={1} />, <StrategyLogo key={2} />]}
        compareItems={[
          [
            <CompareItem type="positive" key={1}>
              <P>
                <strong>
                  Omni routes queries to the nearest data layer to optimize
                  performance.
                </strong>{' '}
                Browser cache, application-layer cache, or the{' '}
                <a href="https://omni.co/blog/under-the-hood-of-omnis-intelligent-cache">
                  data warehouse itself
                </a>
                . No precomputed extracts required.
              </P>
            </CompareItem>,
            <CompareItem type="negative" key={2}>
              <P>
                <strong>Cubes live in the BI server’s memory.</strong> Load too
                many and the whole server slows down.
              </P>
            </CompareItem>,
          ],
          [
            <CompareItem type="positive" key={1}>
              <P>
                <strong>
                  Live queries are the default, and freshness is a setting.
                </strong>{' '}
                Set the{' '}
                <a
                  href="https://docs.omni.co/analyze-explore/performance/caching"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  cache window
                </a>{' '}
                by model or Topic, including zero when every query should hit
                the warehouse.
              </P>
            </CompareItem>,
            <CompareItem type="negative" key={2}>
              <P>
                <strong>Cube data is stale between refreshes.</strong> A refresh
                reloads everything from the warehouse. And a cube defined by a
                metric, like your top 200 stores, can&apos;t refresh
                incrementally at all. Any change means rebuilding the whole
                cube.
              </P>
            </CompareItem>,
          ],
          [
            <CompareItem type="positive" key={1}>
              <P>
                <strong>No cube maintenance.</strong> Query results are{' '}
                <a
                  href="https://docs.omni.co/analyze-explore/performance/caching"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  cached
                </a>{' '}
                for the duration you set, shared between permitted users, and
                configurable per model or Topic. No cube to size or schedule.
              </P>
            </CompareItem>,
            <CompareItem type="negative" key={2}>
              <P>
                <strong>
                  Publishing a large cube requires several times its size in
                  memory.
                </strong>{' '}
                Refreshes get pushed to overnight windows.
              </P>
            </CompareItem>,
          ],
          [
            <CompareItem type="positive" key={1}>
              <P>
                <strong>
                  Model the business the way the business asks questions.
                </strong>{' '}
                <a href="https://omni.co/blog/just-in-time-data-modeling">
                  Just-in-time modeling
                </a>{' '}
                lets you add or update shared definitions as you analyze. The{' '}
                <a href="https://omni.co/blog/announcing-omnis-modeling-agent">
                  Modeling Agent
                </a>{' '}
                drafts model changes for review, and Omni’s{' '}
                <a href="https://omni.co/integrations">integrations</a> keep
                logic interoperable with the rest of your data stack.
              </P>
            </CompareItem>,
            <CompareItem type="negative" key={2}>
              <P>
                <strong>
                  Your team models around the cube, not the business.
                </strong>{' '}
                Size limits dictate the model: metrics to avoid, indexes to
                watch, partitions to tune.
              </P>
            </CompareItem>,
          ],
        ]}
      />

      <CompareGrid
        id="omni-performance-quote"
        oneColumn={true}
        compareItems={[
          [
            <CompareQuote
              key={1}
              type="positive"
              linkURL="https://omni.co/blog/case-study-guitar-center"
              linkLabel="Guitar Center user"
              quote={
                <p>
                  [Omni is] Much better than current reporting systems. Very
                  intuitive and easy to use. Much faster as well.
                </p>
              }
            />,
          ],
        ]}
      />
    </ComparisonCard>
  )
}
